import type { Project } from './types';
import { clipEndTicks } from './selectors';

/**
 * What a ripple drag needs to know about a clip that moved.
 *
 * A drag dispatches a stream of relative moves and only decides to ripple when
 * the pointer comes up, so by then the clips are already at their new positions
 * and where they *came from* has to be carried separately.
 */
export interface MovedClip {
  clipId: string;
  fromTrackId: string;
  fromStartTicks: number;
}

/** A stretch of one track's timeline, in ticks. */
export interface RippleSpan {
  trackId: string;
  startTicks: number;
  endTicks: number;
}

const length = (span: RippleSpan): number => Math.max(0, span.endTicks - span.startTicks);

/**
 * Merges per-track extents into one span each.
 *
 * The span is the whole stretch from the earliest start to the latest end,
 * **including any silence between the clips**. That silence was part of the time
 * the selection occupied, so closing the gap has to close it too; measuring the
 * sum of the clip lengths instead would leave the material behind sitting in a
 * hole the shape of the gaps.
 */
function spansOf(entries: { trackId: string; startTicks: number; endTicks: number }[]): RippleSpan[] {
  const byTrack = new Map<string, RippleSpan>();
  for (const entry of entries) {
    const found = byTrack.get(entry.trackId);
    if (!found) {
      byTrack.set(entry.trackId, { ...entry });
      continue;
    }
    found.startTicks = Math.min(found.startTicks, entry.startTicks);
    found.endTicks = Math.max(found.endTicks, entry.endTicks);
  }
  return [...byTrack.values()].filter((span) => length(span) > 0);
}

/**
 * Where the moved clips were, per track — the gaps a ripple has to close.
 *
 * Lengths come from the clips as they are now, because a move changes where a
 * clip is and never how long it is.
 */
export function liftSpans(project: Project, moved: readonly MovedClip[]): RippleSpan[] {
  return spansOf(
    moved.flatMap((entry) => {
      const clip = project.clips[entry.clipId];
      if (!clip) return [];
      const clipLength = clipEndTicks(project, clip) - clip.startTicks;
      return [
        {
          trackId: entry.fromTrackId,
          startTicks: entry.fromStartTicks,
          endTicks: entry.fromStartTicks + clipLength,
        },
      ];
    }),
  );
}

/** Where the moved clips landed, per track — the gaps a ripple has to open. */
export function insertSpans(project: Project, moved: readonly MovedClip[]): RippleSpan[] {
  return spansOf(
    moved.flatMap((entry) => {
      const clip = project.clips[entry.clipId];
      if (!clip) return [];
      return [{ trackId: clip.trackId, startTicks: clip.startTicks, endTicks: clipEndTicks(project, clip) }];
    }),
  );
}

/**
 * Whether a ripple can be made sense of for this drag.
 *
 * Rippling a selection means treating it as a block: the time it occupied closes
 * up behind it and reopens where it was dropped. That is only honest when the
 * selection **is** the block. If an unselected clip sits inside the stretch the
 * selection covers, closing that stretch would drag it along — moving material
 * the user did not select and, on a tall project, cannot see.
 *
 * So the question is asked of the timeline the clips came from: on every source
 * track, does anything **start** inside the selection's span without being part
 * of it? Starts rather than ends, because a clip is where it begins: one that
 * runs into the span from earlier is behind the material, not in it, and
 * refusing those would rule out an ordinary overlap.
 *
 * This is narrower than dragging a single clip used to be. A clip starting
 * inside another one's span used to ripple, and the material behind would close
 * up by the whole span and land on top of it. Refusing is the better answer, and
 * a test says so.
 *
 * When the answer is no, the drag stays an ordinary move. That is the existing
 * behaviour rather than a failure, and it is what the caller tells the user.
 */
export function canRipple(project: Project, moved: readonly MovedClip[]): boolean {
  if (moved.length === 0) return false;

  const wasMoved = new Map<string, MovedClip>();
  for (const entry of moved) wasMoved.set(entry.clipId, entry);

  for (const span of liftSpans(project, moved)) {
    for (const clip of Object.values(project.clips)) {
      const entry = wasMoved.get(clip.id);
      // Where this clip sat before the drag: moved clips came from somewhere
      // else, everything else has not moved at all.
      const track = entry ? entry.fromTrackId : clip.trackId;
      if (track !== span.trackId) continue;
      if (entry) continue;

      const start = clip.startTicks;
      if (start >= span.startTicks && start < span.endTicks) return false;
    }
  }
  return true;
}

/** True when a drag actually put something somewhere else. */
export function movedAnywhere(project: Project, moved: readonly MovedClip[]): boolean {
  return moved.some((entry) => {
    const clip = project.clips[entry.clipId];
    if (!clip) return false;
    return clip.trackId !== entry.fromTrackId || clip.startTicks !== entry.fromStartTicks;
  });
}
